/**
 * ControlledMultiSelect — a "collect, don't persist" multi-pick control
 * (Item 5).
 *
 * Unlike EditableMultiSelect (which fires a save mutation on every chip
 * add/remove — the org edit screen's direct-write model), this variant is
 * a CONTROLLED input: it holds nothing of its own, surfacing the current
 * selection via `value` + `onChange`. It's used inside SuggestEditForm,
 * where selections are buffered locally and only turned into a suggested
 * edit (a slug delta) on form submit — never persisted on each toggle.
 *
 * Picker UX mirrors EditableMultiSelect: a BottomSheet lists the full
 * vocabulary; tapping toggles a slug in/out and auto-closes (re-open to
 * add more). Selected slugs render as removable chips.
 */

import { useState, type ReactNode } from 'react';
import { BottomSheet } from './primitives/BottomSheet';

export interface ControlledMultiSelectProps {
  /** Currently-selected values (slugs). */
  value: string[];
  /** Allowed values + display labels. Order preserved in the picker. */
  options: ReadonlyArray<{ value: string; label: string }>;
  /** Controlled change callback — receives the next selection. */
  onChange: (next: string[]) => void;
  /** ARIA label + "+ Add {label}" empty-state copy stem (e.g. "subcategory"). */
  label: string;
  disabled?: boolean;
}

export function ControlledMultiSelect({
  value,
  options,
  onChange,
  label,
  disabled = false,
}: ControlledMultiSelectProps): ReactNode {
  const [pickerOpen, setPickerOpen] = useState(false);

  function toggleValue(v: string): void {
    if (value.includes(v)) {
      onChange(value.filter((c) => c !== v));
    } else {
      onChange([...value, v]);
    }
    setPickerOpen(false);
  }

  function removeValue(v: string): void {
    onChange(value.filter((c) => c !== v));
  }

  return (
    <span className="inline-flex flex-wrap items-center gap-1.5">
      {value.map((v) => {
        const opt = options.find((o) => o.value === v);
        return (
          <span key={v} className="inline-flex items-center gap-1">
            <span className="inline-flex items-center gap-1 rounded-lg px-2.5 py-1 text-[11px] font-medium bg-sage-50 text-sage-600">
              {opt?.label ?? v}
            </span>
            <button
              type="button"
              onClick={() => removeValue(v)}
              disabled={disabled}
              aria-label={`Remove ${opt?.label ?? v}`}
              className="text-ink-soft hover:text-ink cursor-pointer text-xs leading-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-500 rounded-sm disabled:opacity-40"
            >
              ✕
            </button>
          </span>
        );
      })}
      <button
        type="button"
        onClick={() => setPickerOpen(true)}
        disabled={disabled}
        aria-label={`Add ${label}`}
        className="inline-flex items-center gap-1 rounded-full border border-dashed border-sage-300 px-2.5 py-1 text-[11px] font-medium text-sage-600 hover:bg-sage-50 hover:border-sage-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-500 transition-colors disabled:opacity-40"
      >
        + Add {label}
      </button>

      <BottomSheet
        isOpen={pickerOpen}
        onClose={() => setPickerOpen(false)}
        title={label}
      >
        <div role="listbox" className="px-5 py-3">
          {options.map((opt) => {
            const isSelected = value.includes(opt.value);
            return (
              <button
                key={opt.value}
                type="button"
                role="option"
                aria-selected={isSelected}
                onClick={() => toggleValue(opt.value)}
                className={`flex w-full items-center gap-3 py-2.5 px-1 border-b border-sage-800/[8%] last:border-0 cursor-pointer text-left ${isSelected ? 'text-sage-700 font-semibold' : 'text-ink'}`}
              >
                <span
                  className={`w-[18px] h-[18px] rounded-md border-2 flex items-center justify-center shrink-0 text-[10px] ${isSelected ? 'border-sage-400 bg-sage-800 text-cream' : 'border-sage-300'}`}
                >
                  {isSelected && '✓'}
                </span>
                <span className="text-sm">{opt.label}</span>
              </button>
            );
          })}
        </div>
      </BottomSheet>
    </span>
  );
}
